import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { api, getBackendOrigin, setBackendOrigin } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { SettingHelp } from '../components/settings/SettingHelp';
import { useTranslation } from '../i18n/I18nContext';
import { applyTheme, type Theme } from '../styles/theme';

function normalizeDisplay(raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  if (!/^https?:\/\//i.test(t)) return `http://${t}`;
  return t;
}

interface SetupStatus {
  needs_setup: boolean;
  wizard_completed: boolean;
  missing_keys: string[];
  settings: Record<string, string>;
}

export function Setup() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { t, language, setLanguage } = useTranslation();

  const existing = getBackendOrigin() || '';
  const [origin, setOrigin] = useState(existing);
  const [connectError, setConnectError] = useState<string | null>(null);

  const [status, setStatus] = useState<SetupStatus | null>(null);
  const [wizardLoading, setWizardLoading] = useState(false);
  const [wizardError, setWizardError] = useState<string | null>(null);
  const [wizardMessage, setWizardMessage] = useState<string | null>(null);
  const [form, setForm] = useState({
    discovery_host_ip: '',
    api_username: '',
    api_password: '',
    refresh_interval: '30',
    theme: 'dark',
    language,
    analytics_db_url: '',
    finance_db_host: '',
    finance_db_port: '5432',
    finance_db_user: '',
    finance_db_password: '',
    finance_db_name: '',
    app_root_dir: '',
    freqtrade_root_dir: '',
    freqtrade_data_dir: '',
    freqtrade_agent_root_dir: '',
    fred_api_key: '',
    newsapi_key: '',
  });

  const example = useMemo(() => {
    if (typeof window === 'undefined') return 'http://your-server:8099';
    return `${window.location.protocol}//${window.location.host}`;
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    async function loadStatus() {
      setWizardLoading(true);
      setWizardError(null);
      try {
        const response = await api.get<SetupStatus>('/settings/setup-status');
        if (cancelled) return;
        const setupStatus = response.data;
        const nextLanguage = (setupStatus.settings.language as 'de' | 'en') || language;
        setLanguage(nextLanguage);
        setStatus(setupStatus);
        setForm({
          discovery_host_ip: setupStatus.settings.discovery_host_ip || '',
          api_username: setupStatus.settings.api_username || '',
          api_password: setupStatus.settings.api_password || '',
          refresh_interval: setupStatus.settings.refresh_interval || '30',
          theme: setupStatus.settings.theme || 'industrial',
          language: nextLanguage,
          analytics_db_url: setupStatus.settings.analytics_db_url || '',
          finance_db_host: setupStatus.settings.finance_db_host || '',
          finance_db_port: setupStatus.settings.finance_db_port || '5432',
          finance_db_user: setupStatus.settings.finance_db_user || '',
          finance_db_password: setupStatus.settings.finance_db_password || '',
          finance_db_name: setupStatus.settings.finance_db_name || '',
          app_root_dir: setupStatus.settings.app_root_dir || '',
          freqtrade_root_dir: setupStatus.settings.freqtrade_root_dir || '',
          freqtrade_data_dir: setupStatus.settings.freqtrade_data_dir || '',
          freqtrade_agent_root_dir: setupStatus.settings.freqtrade_agent_root_dir || '',
          fred_api_key: setupStatus.settings.fred_api_key || '',
          newsapi_key: setupStatus.settings.newsapi_key || '',
        });
      } catch (error) {
        if (!cancelled) {
          setWizardError(error instanceof Error ? error.message : 'Failed to load setup status');
        }
      } finally {
        if (!cancelled) {
          setWizardLoading(false);
        }
      }
    }

    void loadStatus();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, language, setLanguage]);

  useEffect(() => {
    setForm((prev) => ({ ...prev, language }));
  }, [language]);

  const saveConnection = () => {
    setConnectError(null);
    const value = origin.trim();
    if (!value) {
      setConnectError(t('setup.urlRequired'));
      return;
    }
    const normalized = normalizeDisplay(value).replace(/\/+$/, '');
    if (!/^https?:\/\//i.test(normalized)) {
      setConnectError(t('setup.urlSchemeRequired'));
      return;
    }
    setBackendOrigin(normalized);
    window.location.href = '/login';
  };

  const clearConnection = () => {
    setBackendOrigin(null);
    setOrigin('');
    setConnectError(t('setup.clearNotice'));
  };

  const saveWizard = async () => {
    setWizardLoading(true);
    setWizardError(null);
    setWizardMessage(null);

    try {
      await api.post('/settings/batch', {
        discovery_host_ip: form.discovery_host_ip.trim(),
        api_username: form.api_username.trim(),
        api_password: form.api_password.trim(),
        refresh_interval: form.refresh_interval,
        theme: form.theme,
        language: form.language,
        analytics_db_url: form.analytics_db_url.trim(),
        finance_db_host: form.finance_db_host.trim(),
        finance_db_port: form.finance_db_port.trim(),
        finance_db_user: form.finance_db_user.trim(),
        finance_db_password: form.finance_db_password,
        finance_db_name: form.finance_db_name.trim(),
        app_root_dir: form.app_root_dir.trim(),
        freqtrade_root_dir: form.freqtrade_root_dir.trim(),
        freqtrade_data_dir: form.freqtrade_data_dir.trim(),
        freqtrade_agent_root_dir: form.freqtrade_agent_root_dir.trim(),
        fred_api_key: form.fred_api_key.trim(),
        newsapi_key: form.newsapi_key.trim(),
        install_wizard_completed: 'true',
      });
      setWizardMessage(t('setup.saved'));
      navigate('/');
    } catch (error) {
      setWizardError(error instanceof Error ? error.message : 'Failed to save setup');
    } finally {
      setWizardLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-6">
        <div className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">{t('setup.connectTitle')}</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            {t('setup.connectDescription')}
          </p>

          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              {t('setup.dashboardUrl')}
            </label>
            <input
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              placeholder={example}
              className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
            {connectError ? (
              <div className="mt-2 text-sm text-red-500">{connectError}</div>
            ) : null}
          </div>

          <div className="mt-5 flex gap-2">
            <button
              onClick={saveConnection}
              className="flex-1 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            >
              {t('setup.saveContinue')}
            </button>
            <button
              onClick={clearConnection}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
            >
              {t('common.clear')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status && !status.needs_setup) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('setup.title')}
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          {t('setup.description')}
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        {wizardLoading && !status ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('setup.loadingStatus')}</p>
        ) : null}

        {wizardError ? (
          <p className="mb-4 text-sm text-red-500">{wizardError}</p>
        ) : null}

        {status?.missing_keys?.length ? (
          <div className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
            {t('setup.missingRequired')}: {status.missing_keys.join(', ')}
          </div>
        ) : null}

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('setup.language')}
            </label>
            <select
              value={form.language}
              onChange={(e) => {
                const nextLanguage = e.target.value as 'de' | 'en';
                setForm((prev) => ({ ...prev, language: nextLanguage }));
                setLanguage(nextLanguage);
              }}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="de">{t('language.german')}</option>
              <option value="en">{t('language.english')}</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('setup.discoveryHost')}
            </label>
            <input
              value={form.discovery_host_ip}
              onChange={(e) => setForm((prev) => ({ ...prev, discovery_host_ip: e.target.value }))}
              placeholder="server-ip-or-dns"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              The IP address the backend should use to reach your Freqtrade APIs.
            </p>
            <SettingHelp settingKey="discovery_host_ip" />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('setup.botApiUsername')}
            </label>
            <input
              value={form.api_username}
              onChange={(e) => setForm((prev) => ({ ...prev, api_username: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <SettingHelp settingKey="api_username" />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('setup.botApiPassword')}
            </label>
            <input
              type="password"
              value={form.api_password}
              onChange={(e) => setForm((prev) => ({ ...prev, api_password: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <SettingHelp settingKey="api_password" />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('setup.refreshInterval')}
            </label>
            <select
              value={form.refresh_interval}
              onChange={(e) => setForm((prev) => ({ ...prev, refresh_interval: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="15">{t('setup.seconds15')}</option>
              <option value="30">{t('setup.seconds30')}</option>
              <option value="60">{t('setup.seconds60')}</option>
              <option value="120">{t('setup.seconds120')}</option>
            </select>
            <SettingHelp settingKey="refresh_interval" />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('setup.theme')}
            </label>
            <select
              value={form.theme}
              onChange={(e) => {
                const nextTheme = e.target.value as Theme;
                setForm((prev) => ({ ...prev, theme: nextTheme }));
                applyTheme(nextTheme);
              }}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="classic">{t('theme.classic')}</option>
              <option value="industrial">{t('theme.industrial')}</option>
            </select>
          </div>

          <div className="md:col-span-2 pt-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('setup.infrastructure')}</h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('setup.infrastructureHint')}
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings.analyticsDbUrl')}
            </label>
            <input
              value={form.analytics_db_url}
              onChange={(e) => setForm((prev) => ({ ...prev, analytics_db_url: e.target.value }))}
              placeholder="postgresql://user:password@host:5432/freqtrade_analytics"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <SettingHelp settingKey="analytics_db_url" />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings.financeDbHost')}
            </label>
            <input
              value={form.finance_db_host}
              onChange={(e) => setForm((prev) => ({ ...prev, finance_db_host: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <SettingHelp settingKey="finance_db_host" />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings.financeDbPort')}
            </label>
            <input
              value={form.finance_db_port}
              onChange={(e) => setForm((prev) => ({ ...prev, finance_db_port: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <SettingHelp settingKey="finance_db_port" />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings.financeDbUser')}
            </label>
            <input
              value={form.finance_db_user}
              onChange={(e) => setForm((prev) => ({ ...prev, finance_db_user: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <SettingHelp settingKey="finance_db_user" />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings.financeDbPassword')}
            </label>
            <input
              type="password"
              value={form.finance_db_password}
              onChange={(e) => setForm((prev) => ({ ...prev, finance_db_password: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <SettingHelp settingKey="finance_db_password" />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings.financeDbName')}
            </label>
            <input
              value={form.finance_db_name}
              onChange={(e) => setForm((prev) => ({ ...prev, finance_db_name: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <SettingHelp settingKey="finance_db_name" />
          </div>

          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings.appRootDir')}
            </label>
            <input
              value={form.app_root_dir}
              onChange={(e) => setForm((prev) => ({ ...prev, app_root_dir: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <SettingHelp settingKey="app_root_dir" />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings.freqtradeRootDir')}
            </label>
            <input
              value={form.freqtrade_root_dir}
              onChange={(e) => setForm((prev) => ({ ...prev, freqtrade_root_dir: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <SettingHelp settingKey="freqtrade_root_dir" />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings.freqtradeDataDir')}
            </label>
            <input
              value={form.freqtrade_data_dir}
              onChange={(e) => setForm((prev) => ({ ...prev, freqtrade_data_dir: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <SettingHelp settingKey="freqtrade_data_dir" />
          </div>

          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings.agentRootDir')}
            </label>
            <input
              value={form.freqtrade_agent_root_dir}
              onChange={(e) => setForm((prev) => ({ ...prev, freqtrade_agent_root_dir: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <SettingHelp settingKey="freqtrade_agent_root_dir" />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings.fredApiKey')}
            </label>
            <input
              value={form.fred_api_key}
              onChange={(e) => setForm((prev) => ({ ...prev, fred_api_key: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <SettingHelp settingKey="fred_api_key" />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('settings.newsApiKey')}
            </label>
            <input
              value={form.newsapi_key}
              onChange={(e) => setForm((prev) => ({ ...prev, newsapi_key: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
            <SettingHelp settingKey="newsapi_key" />
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <button
            onClick={saveWizard}
            disabled={wizardLoading}
            className="rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {wizardLoading ? t('setup.saving') : t('setup.save')}
          </button>

          <button
            onClick={() => navigate('/')}
            className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {t('setup.skip')}
          </button>
        </div>

        {wizardMessage ? (
          <p className="mt-4 text-sm text-green-600 dark:text-green-400">{wizardMessage}</p>
        ) : null}
      </div>
    </div>
  );
}
